import React from "react";
import { ChevronDown, ChevronUp, ArrowUp, ArrowDown } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// ─── Shared utility ──────────────────────────────────────────────────────────

export const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Math.abs(amount));

/** Maps legacy palette tokens (e.g. from older themed APIs) to Tailwind classes. */
function paletteTokenToClass(color?: string): string {
  if (!color) return "";
  const map: Record<string, string> = {
    "text.secondary": "text-muted-foreground",
    "text.disabled": "text-muted-foreground/70",
    "success.main": "text-green-600 dark:text-green-400",
    "error.main": "text-red-600 dark:text-red-400",
    "primary.main": "text-primary",
    "grey.600": "text-muted-foreground",
    "info.main": "text-sky-600 dark:text-sky-400",
  };
  return map[color] ?? "";
}

// ─── Cell definitions ─────────────────────────────────────────────────────────

export type TextCellDef = {
  type?: "text";
  value: React.ReactNode;
  align?: "left" | "right" | "center";
  color?: string;
  fontWeight?: number;
  truncate?: number;
  fontSize?: string;
  tooltip?: string;
  onClick?: () => void;
};

export type AmountCellDef = {
  type: "amount";
  amount: number;
  transactionType: "credit" | "debit";
};

export type ChipCellDef = {
  type: "chip";
  value: string | number;
  color?: "primary" | "secondary" | "success" | "error" | "info" | "warning" | "default";
  variant?: "filled" | "outlined";
};

export type TagsCellDef = {
  type: "tags";
  tags?: string;
  onTagClick?: (tag: string) => void;
};

export type CellDef = TextCellDef | AmountCellDef | ChipCellDef | TagsCellDef;

function chipBadgeClass(color?: ChipCellDef["color"], variant?: ChipCellDef["variant"]): string {
  const base = "h-5 text-[0.7rem] font-normal";
  if (variant === "filled") {
    switch (color) {
      case "success":
        return cn(base, "border-transparent bg-green-600 text-white hover:bg-green-600/90");
      case "error":
        return cn(base, "border-transparent bg-destructive text-destructive-foreground");
      case "primary":
        return cn(base, "border-transparent bg-primary text-primary-foreground");
      case "warning":
        return cn(base, "border-transparent bg-amber-500 text-white");
      case "info":
        return cn(base, "border-transparent bg-sky-600 text-white");
      default:
        return cn(base, "border-transparent bg-secondary text-secondary-foreground");
    }
  }
  switch (color) {
    case "success":
      return cn(base, "border-green-600/30 text-green-700 dark:text-green-400");
    case "error":
      return cn(base, "border-destructive/50 text-destructive");
    case "primary":
      return cn(base, "border-primary/50 text-primary");
    case "warning":
      return cn(base, "border-amber-500/50 text-amber-700 dark:text-amber-400");
    case "info":
      return cn(base, "border-sky-500/50 text-sky-700 dark:text-sky-400");
    default:
      return cn(base, "");
  }
}

export const renderCell = (
  cell: CellDef,
  i: number,
  opts?: { textClass?: string }
): React.ReactNode => {
  const textClass = opts?.textClass ?? "text-sm";
  switch (cell.type) {
    case "amount":
      return (
        <TableCell key={i} className="whitespace-nowrap text-right">
          <div className="flex items-center justify-end gap-1">
            {cell.transactionType === "credit" ? (
              <ArrowUp className="h-3.5 w-3.5 shrink-0 text-green-600 dark:text-green-400" />
            ) : (
              <ArrowDown className="h-3.5 w-3.5 shrink-0 text-red-600 dark:text-red-400" />
            )}
            <span className="sr-only">{cell.transactionType}</span>
            <span
              className={cn(
                textClass,
                "font-medium",
                cell.transactionType === "credit"
                  ? "text-green-600 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              )}
            >
              {formatCurrency(cell.amount)}
            </span>
          </div>
        </TableCell>
      );

    case "chip":
      return (
        <TableCell key={i}>
          <Badge variant="outline" className={chipBadgeClass(cell.color, cell.variant)}>
            {cell.value}
          </Badge>
        </TableCell>
      );

    case "tags": {
      const tags = cell.tags?.split(", ").filter(Boolean) ?? [];
      const { onTagClick } = cell;
      if (tags.length === 0) return <TableCell key={i} />;
      return (
        <TableCell key={i}>
          <div className="flex flex-wrap gap-1">
            {tags.slice(0, 3).map((tag, j) => (
              <Badge
                key={j}
                variant="outline"
                className={cn(
                  "h-5 cursor-default px-1.5 text-[0.7rem] font-normal",
                  onTagClick && "cursor-pointer hover:bg-accent"
                )}
                onClick={
                  onTagClick
                    ? (e) => {
                        e.stopPropagation();
                        onTagClick(tag);
                      }
                    : undefined
                }
              >
                {tag.replace(/_/g, " ")}
              </Badge>
            ))}
            {tags.length > 3 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-[0.7rem] font-normal">
                +{tags.length - 3}
              </Badge>
            )}
          </div>
        </TableCell>
      );
    }

    default: {
      const { onClick } = cell;
      const text = (
        <span
          className={cn(
            textClass,
            cell.truncate && "block truncate",
            cell.onClick && "cursor-pointer hover:text-primary hover:underline",
            paletteTokenToClass(cell.color),
            cell.fontWeight && cell.fontWeight >= 600 && "font-semibold"
          )}
          style={{
            ...(cell.truncate ? { maxWidth: cell.truncate } : {}),
            ...(cell.fontSize ? { fontSize: cell.fontSize } : {}),
            ...(!paletteTokenToClass(cell.color) && cell.color?.startsWith("#")
              ? { color: cell.color }
              : {}),
          }}
          onClick={
            onClick
              ? (e) => {
                  e.stopPropagation();
                  onClick();
                }
              : undefined
          }
        >
          {cell.value}
        </span>
      );
      return (
        <TableCell
          key={i}
          className={cn(
            "min-w-0 overflow-hidden",
            cell.align === "right" && "text-right",
            cell.align === "center" && "text-center"
          )}
        >
          {cell.tooltip ? (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>{text}</TooltipTrigger>
                <TooltipContent side="top" className="max-w-sm">
                  {cell.tooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : typeof cell.value === "string" || typeof cell.value === "number" ? (
            text
          ) : (
            <div className="w-full min-w-0 overflow-hidden">{cell.value}</div>
          )}
        </TableCell>
      );
    }
  }
};

// ─── Detail definitions ───────────────────────────────────────────────────────

export type DetailField = {
  label: string;
  value: string;
};

export type DetailMetadataEntry = {
  key: string;
  value: string;
  onClick?: () => void;
};

export type FieldsDetailDef = {
  type: "fields";
  fields: DetailField[];
  metadata?: {
    title?: string;
    entries: DetailMetadataEntry[];
  };
};

export type TableDetailDef = {
  type: "table";
  caption?: string;
  headers: string[];
  rows: CellDef[][];
};

export type DetailDef = FieldsDetailDef | TableDetailDef;

const renderDetail = (detail: DetailDef): React.ReactNode => {
  if (detail.type === "fields") {
    return (
      <div className="bg-muted/40 p-4">
        {detail.fields.map(({ label, value }) => (
          <React.Fragment key={label}>
            <p className="mb-1 text-sm font-semibold">{label}</p>
            <p className="mb-3 text-sm">{value}</p>
          </React.Fragment>
        ))}
        {detail.metadata && detail.metadata.entries.length > 0 && (
          <div className="mt-2 rounded-md bg-muted/80 p-2">
            <p className="mb-1 text-xs font-semibold text-muted-foreground">
              {detail.metadata.title ?? "Tag Metadata"}
            </p>
            {detail.metadata.entries.map(({ key, value, onClick }) => (
              <p
                key={key}
                className={cn(
                  "text-xs text-muted-foreground",
                  onClick && "cursor-pointer hover:text-primary"
                )}
                onClick={onClick}
              >
                {key}: <strong>{value}</strong>
              </p>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-muted/40 px-6 py-2">
      {detail.caption && (
        <p className="mb-2 block text-xs text-muted-foreground">{detail.caption}</p>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            {detail.headers.map((h) => (
              <TableHead key={h} className="h-8 text-xs">
                {h}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {detail.rows.map((row, i) => (
            <TableRow key={i}>{row.map((cell, j) => renderCell(cell, j))}</TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

interface ExpandableRowProps {
  isExpanded: boolean;
  onToggle: () => void;
  cells: CellDef[];
  detail: DetailDef;
  /** Match denser panels (e.g. AI Analysis positions column at 0.75rem). */
  compact?: boolean;
  /**
   * Emphasized (a link's rows): a primary tint and left bar, the underwriting views' emphasis
   * (AnalysisView's hovered line). Marked `data-highlighted` so a table can scroll to it.
   */
  highlighted?: boolean;
  /** A cell before the chevron (a row's checkbox); clicks in it don't toggle the row. */
  leading?: React.ReactNode;
  className?: string;
}

const ExpandableRow: React.FC<ExpandableRowProps> = ({
  isExpanded,
  onToggle,
  cells,
  detail,
  compact = false,
  highlighted = false,
  leading,
  className,
}) => {
  const textClass = compact ? "text-[0.75rem]" : "text-sm";
  return (
    <>
      <TableRow
        className={cn(
          "cursor-pointer",
          highlighted && "bg-primary/15 hover:bg-primary/20",
          className
        )}
        data-highlighted={highlighted || undefined}
        onClick={onToggle}
      >
        {leading !== undefined && (
          <TableCell className="w-7 p-0 pl-2" onClick={(event) => event.stopPropagation()}>
            {leading}
          </TableCell>
        )}
        <TableCell
          className={cn("w-6 p-0 pl-1", highlighted && "border-l-[3px] border-l-primary")}
          data-highlight-bar={highlighted || undefined}
          data-row-toggle
        >
          <Button type="button" variant="ghost" size="icon" className="h-6 w-6">
            {isExpanded ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </Button>
        </TableCell>
        {cells.map((cell, i) => renderCell(cell, i, { textClass }))}
      </TableRow>
      {isExpanded && (
        <TableRow className="border-b hover:bg-transparent">
          <TableCell colSpan={cells.length + (leading !== undefined ? 2 : 1)} className="p-0">
            {renderDetail(detail)}
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default ExpandableRow;
